import { gql, useMutation, useQuery } from '@apollo/client';
import { ItemStatuses } from 'constants/statuses';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AssurantEligibleOutput } from '@updater/consumer-graph';
import { CreateItem, CreateItemVariables } from 'types/generated/CreateItem';
import { Statuses } from 'types/generated/globalTypes';
import { useSWRWhenAuthed } from 'utils/use-swr-when-authed';
import { ItemKinds } from 'types/item-kinds';

interface UseAssurantData {
  loading: boolean;
  eligible?: boolean;
  variant?: AssurantEligibleOutput['variant'];
  error?: unknown;
}

const ASSURANT_ELIGIBILITY_QUERY = gql`
  query AssurantEligibility($itemId: ID!) {
    assurantEligibility(itemId: $itemId) {
      eligible
      ineligibleReason
      variant
    }
  }
`;

const CREATE_INSURANCE_ITEM_MUTATION = gql`
  mutation CreateItem($input: CreateItemInput!) {
    createItem(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_POLICY_MUTATION = gql`
  mutation CreateAssurantPolPolicy(
    $itemId: ID!
    $additionalInsured: [AdditionalInsuredInput!]
  ) {
    createAssurantPolPolicy(
      itemId: $itemId
      additionalInsured: $additionalInsured
    ) {
      coverageDetails {
        liabilityAmount
        personalPropertyAmount
      }
      transactionDetails {
        policyId
        referralId
        responseMessage
      }
      policyEffectiveDate
      premiumDetails {
        amount
      }
      additionalInsured {
        firstName
        lastName
        residentId
      }
    }
  }
`;

// useAssurant() is mounted by many components at once (NavigationLayout, the
// essentials tab cards, etc. all reach it through useDataForCards), and
// `useMutation`'s `called` flag is per instance. Without a shared guard every
// instance sees "no insurance item yet" and creates its own.
//
// The promise resolves to the created item's id. Instances that skip because
// a create is already in flight use that id directly instead of waiting for
// /v2/items to be refetched, so they never depend on the refetch containing
// the new item. The guard itself is cleared only after the refetch settles, so
// a late instance sees the new item instead of creating another.
//
// It resolves to either the new id or the failure, so a failed create reaches
// every instance as an error instead of leaving them on a spinner.
type InsuranceItemCreation = { id: string } | { error: unknown };
let insuranceItemCreation: Promise<InsuranceItemCreation> | null = null;

/**
 * Hook to retrieve whether a user is eligible for Assurant flow or not
 * NOTE:
 * depending on the type of insurance ( ROB vs regular ), they may not be any item created on signup
 * so we need to create the item first
 * @returns {Object} {loading, enabled, error, itemData }
 */

export function useAssurant(): UseAssurantData {
  const {
    data: itemData,
    error: itemError,
    mutate,
  } = useSWRWhenAuthed('/v2/items');
  const { data: itemDefinitions, error: itemDefinitionsError } =
    useSWRWhenAuthed('/v2/item_definitions');
  const [createItem, { data: createItemData, called: createItemCalled }] =
    useMutation<CreateItem, CreateItemVariables>(
      CREATE_INSURANCE_ITEM_MUTATION
    );
  useEffect(() => {
    if (createItemData) {
      mutate();
    }
  }, [createItemData, mutate]);
  const item = itemData?.find(
    ({ type }: { type: string }) => type === 'insurance'
  );
  // set from the shared in-flight create, for instances that didn't start it
  const [createdItemId, setCreatedItemId] = useState<string | undefined>();
  const [createError, setCreateError] = useState<unknown>();
  // Only unmount stops us applying the shared create result. It must not be tied
  // to the effect below: `useMutation` flips `called` as soon as the create
  // starts, which re-runs (and cleans up) that effect before the result lands.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const itemId = item?.id || createItemData?.createItem?.id || createdItemId;

  const {
    data: eligibilityData,
    called: eligibilityCalled,
    error: eligibilityError,
  } = useQuery(ASSURANT_ELIGIBILITY_QUERY, {
    variables: {
      itemId,
    },
    skip: itemId === undefined && item?.status !== ItemStatuses.STARTED,
  });

  // undefined until the definitions load, so we never create on a guess
  const insuranceEnabled = itemDefinitions
    ? itemDefinitions.some(
        ({ code, enabled }: { code: string; enabled: boolean }) =>
          code === ItemKinds.INSURANCE && enabled
      )
    : undefined;
  // `item` is also undefined while /v2/items is still loading, so wait for it
  // before concluding the resident has none
  const insuranceDisabledWithoutItem =
    insuranceEnabled === false && itemData !== undefined && !item;

  const itemDataExisty = !!itemData;
  const needToCreateItemId =
    itemDataExisty && insuranceEnabled === true && !itemId && !createItemCalled;

  useEffect(() => {
    if (!needToCreateItemId) {
      return;
    }
    if (!insuranceItemCreation) {
      const creation: Promise<InsuranceItemCreation> = createItem({
        variables: {
          input: {
            kind: 'insurance',
            status: Statuses.started,
          },
        },
      })
        .then((result): InsuranceItemCreation => {
          const id = result.data?.createItem?.id;
          return id === undefined || id === null
            ? { error: new Error('createItem returned no item id') }
            : { id: String(id) };
        })
        .catch((error): InsuranceItemCreation => {
          console.warn(error);
          return { error };
        });
      insuranceItemCreation = creation;
      creation
        .then(() => mutate())
        .catch((e) => console.warn(e))
        .finally(() => {
          if (insuranceItemCreation === creation) {
            insuranceItemCreation = null;
          }
        });
    }
    insuranceItemCreation.then((creation) => {
      if (!mounted.current) {
        return;
      }
      if ('id' in creation) {
        setCreatedItemId(creation.id);
      } else {
        setCreateError(creation.error);
      }
    });
  }, [needToCreateItemId, createItem, mutate]);

  const eligible =
    eligibilityData?.assurantEligibility?.eligible ||
    !!item?.metadata?.completed_insurance_type?.includes('assurant');
  // the boolean above might be false when eligibilityData is undefined and we still don't have the item from the network call, eg: undefined || false
  // which can lead to this hook prematurely returning false for eligibility, so we create a stable definition of not eligible
  // so that we only return eliglble = false when we know its the case from the graphql call
  const notEligible =
    eligibilityData !== undefined &&
    !eligibilityData?.assurantEligibility?.eligible;

  const variant = eligibilityData?.assurantEligibility?.variant;

  return useMemo(() => {
    if (eligible) {
      return { loading: false, eligible, variant, error: undefined };
    }
    if (notEligible) {
      return {
        loading: false,
        eligible: false,
        variant: undefined,
        error: undefined,
      };
    }
    // insurance is turned off for this property and the resident has no item,
    // so there is nothing to check eligibility against
    if (insuranceDisabledWithoutItem) {
      return {
        loading: false,
        eligible: false,
        variant: undefined,
        error: undefined,
      };
    }
    const definitionsError = !item ? itemDefinitionsError : undefined;
    // a failed create is stale once an item exists (e.g. another mount retried)
    const activeCreateError = itemId ? undefined : createError;
    if (
      itemError ||
      eligibilityError ||
      definitionsError ||
      activeCreateError
    ) {
      return {
        loading: false,
        eligible: undefined,
        variant: undefined,
        error:
          itemError ||
          eligibilityError ||
          definitionsError ||
          activeCreateError,
      };
    }
    if (eligibilityCalled && !itemData) {
      return {
        loading: true,
        eligible: undefined,
        variant: undefined,
        error: undefined,
      };
    }
    return {
      loading: true,
      eligible: undefined,
      variant: undefined,
      error: undefined,
    };
  }, [
    eligible,
    variant,
    notEligible,
    eligibilityCalled,
    itemData,
    itemError,
    eligibilityError,
    insuranceDisabledWithoutItem,
    itemDefinitionsError,
    createError,
    itemId,
    item,
  ]);
}
